/**
 * Auth API Functions (using Generated SDK)
 * ฟังก์ชัน API สำหรับการยืนยันตัวตน (ใช้ SDK ที่สร้างอัตโนมัติ)
 */

import { Api2, Api3 } from '@/services/client'
import type {
  LoginInput,
  User,
  UpdateProfileInput,
  ChangePasswordInput,
  ForgotPasswordInput,
  ResetPasswordInput,
} from './types'

/**
 * Handle SDK error and extract message
 */
function handleError(error: unknown, defaultMessage: string): never {
  if (error && typeof error === 'object' && 'message' in error) {
    throw new Error(String(error.message))
  }
  throw new Error(defaultMessage)
}

export const authApi = {
  /**
   * Login with email and password.
   * เข้าสู่ระบบด้วยอีเมลและรหัสผ่าน
   */
  login: async (input: LoginInput): Promise<{ accessToken: string; user: User }> => {
    try {
      const { data } = await Api2.postAuthLogin({
        body: input as unknown as never,
        throwOnError: true,
      })
      return data as unknown as { accessToken: string; user: User }
    } catch (error) {
      handleError(error, 'Login failed')
    }
  },

  /**
   * Refresh access token using HTTP-only cookie.
   * รีเฟรช access token โดยใช้ HTTP-only cookie
   */
  refreshToken: async (): Promise<{ accessToken: string; user: User }> => {
    try {
      const { data } = await Api2.postAuthRefresh({
        throwOnError: true,
      })
      return data as unknown as { accessToken: string; user: User }
    } catch (error) {
      handleError(error, 'Token refresh failed')
    }
  },

  /**
   * Logout (invalidate session on server).
   * ออกจากระบบ (ยกเลิกเซสชันบนเซิร์ฟเวอร์)
   */
  logout: async (): Promise<void> => {
    if (import.meta.env.DEV) return
    try {
      await Api2.postAuthLogout({
        throwOnError: true,
      })
    } catch {
      // Ignore errors during logout
    }
  },

  /**
   * Get current user profile.
   * ดึงข้อมูลโปรไฟล์ผู้ใช้ปัจจุบัน
   */
  getCurrentUser: async (): Promise<User> => {
    try {
      const { data } = await Api3.getUsersMe({
        throwOnError: true,
      })
      return data as unknown as User
    } catch (error) {
      handleError(error, 'Failed to get current user')
    }
  },

  /**
   * Update user profile.
   * อัปเดตโปรไฟล์ผู้ใช้
   */
  updateProfile: async (data: UpdateProfileInput): Promise<User> => {
    try {
      const { data: responseData } = await Api3.patchUsersMe({
        body: data,
        throwOnError: true,
      })
      return responseData as unknown as User
    } catch (error) {
      handleError(error, 'Failed to update profile')
    }
  },

  /**
   * Change password.
   * เปลี่ยนรหัสผ่าน
   */
  changePassword: async (data: ChangePasswordInput): Promise<void> => {
    try {
      await Api3.postUsersMePassword({
        body: data,
        throwOnError: true,
      })
    } catch (error) {
      handleError(error, 'Failed to change password')
    }
  },

  /**
   * Upload avatar.
   * อัปโหลดรูปโปรไฟล์
   */
  uploadAvatar: async (file: File): Promise<{ avatarUrl: string }> => {
    try {
      const { data } = await Api3.postUsersMeAvatar({
        body: { avatar: file } as unknown as never,
        throwOnError: true,
      })
      return data as unknown as { avatarUrl: string }
    } catch (error) {
      handleError(error, 'Failed to upload avatar')
    }
  },

  /**
   * Request password reset email.
   * ส่งคำขอรีเซ็ตรหัสผ่านทางอีเมล
   */
  requestPasswordReset: async (data: ForgotPasswordInput): Promise<void> => {
    try {
      await Api2.postAuthForgotPassword({
        body: data,
        throwOnError: true,
      })
    } catch (error) {
      handleError(error, 'Failed to send reset email')
    }
  },

  /**
   * Reset password with token.
   * รีเซ็ตรหัสผ่านด้วย token
   */
  resetPassword: async (data: ResetPasswordInput): Promise<void> => {
    try {
      await Api2.postAuthResetPassword({
        body: data,
        throwOnError: true,
      })
    } catch (error) {
      handleError(error, 'Failed to reset password')
    }
  },
}
